import PropTypes from 'prop-types';
import React from 'react';
import styled from 'styled-components';

import { includes } from 'lib/helpers';

import { ScrollerContext } from './context';

import { DEFAULT, SCROLLER_CLASSNAME, SCROLLING_MODULE_CLASSNAME } from './constants';

const STOP_TIMEOUT_DELAY = 2000;

/**
 * Some info custom Scroller usage in the readme
 * Talk to IB dev team with further questions
 */

class ScrollerWithContextProvider extends React.Component {
	static contextType = ScrollerContext;

	constructor(props, context) {
		super(props);
		this.id = props.id || DEFAULT;
		const registeredScrollers = !!context[this.id];
		const idAlreadyExists = includes(registeredScrollers || [], this.id);

		this.scrollerRef = React.createRef({ scrollTop: 0 });
		this.Component = props.scrollingComponent || styled.div``;
		this.Context = React.createContext({});
		this.raf = null;
		this.timeout = null;
		this.modules = {};

		if (idAlreadyExists) {
			// eslint-disable-next-line no-console
			console.warn(
				`Scroller with id ${this.id} is already in use. You may only register one default Scroller, which does not require an id. Additional Scrollers must be passed a unqiue id and a scrollingElement (id to query or DOM node).`,
			);
		}

		const moduleClassName = `${this.id}-${SCROLLING_MODULE_CLASSNAME}`;
		const enableScroller = () => this.setState({ enabled: true });
		const disableScroller = () => this.setState({ enabled: false });
		const setScrollerReady = () => this.setState({ ready: true });
		const setScrollerNotReady = () => this.setState({ ready: false });
		this.setScrollerReady = setScrollerReady;
		this.setScrollerNotReady = setScrollerNotReady;

		const registerModule = (moduleId, module) => {
			this.modules = {
				...this.modules,
				[moduleId]: module,
			};

			const moduleEls = this.state.scrollingElement?.getElementsByClassName(moduleClassName) || [];
			const numModulesRegistered = Object.keys(this.modules).length;
			const numModulesOnDOM = moduleEls.length;
			const isReady = !!numModulesRegistered && !!numModulesOnDOM && numModulesRegistered === numModulesOnDOM;

			if (isReady) this.setScrollerReady();
			if (!isReady) this.setScrollerNotReady();
		};

		const unregisterModule = (id) => {
			const newModules = { ...this.modules };
			delete newModules[id];
			this.modules = newModules;
		};

		this.state = {
			id: this.id,
			scrollingElement: null,
			ready: false,
			enabled: props.defaultEnabled || true,
			moduleClassName,
			registerModule,
			unregisterModule,
			enableScroller,
			disableScroller,
			setScrollerReady,
			setScrollerNotReady,
			forceUpdate: this.forceUpdate,
			forceResize: this.forceResize,
			changeScroll: this.props.changeScroll || null,
		};

		context.setScrollerContext(this.id, this.Context);
	}

	componentDidMount() {
		const scrollingElement = this.id === DEFAULT ? document.documentElement || document.body : this.scrollerRef.current;
		if (!this.state.enabled) {
			scrollingElement.style['overflow-y'] = 'hidden';
		}
		this.scrollingElement = scrollingElement;

		const elementForListener = this.id === DEFAULT ? document : scrollingElement;
		elementForListener.addEventListener('scroll', this.onScroll);
		addEventListener('resize', this.onResize);

		this.setState({ scrollingElement });
	}

	componentDidUpdate(prevProps, prevState) {
		const { enabled, ready } = this.state;

		const disable = !enabled && prevState.enabled;
		const enable = enabled && !prevState.enabled;
		if (disable) {
			this.disable();
		} else if (enable) {
			this.enable();
		}

		const doReady = ready && !prevState.ready;
		const noReady = !ready && prevState.ready;
		if (doReady) {
			this.onResize();
		} else if (noReady) {
			this.stop();
		}
	}

	onScroll = (event) => {
		const scrollNum = event.target['scrollTop'];
		if (this.state.changeScroll) this.state.changeScroll(scrollNum);

		const { ready, enabled } = this.state;
		if (!ready || !enabled) return;
		this.setStopTimeout();
		this.start();
	};

	onResize = (event) => {
		if (!this.state.ready || !this.state.enabled) return;
		Object.values(this.modules).forEach(({ onResize }) => {
			onResize(event);
		});
	};

	disable = () => {
		this.scrollingElement.style['overflow-y'] = 'hidden';
	};

	enable = () => {
		this.scrollingElement.style['overflow-y'] = 'scroll';
	};

	start = () => {
		if (!this.raf) {
			this.update();
		}
	};

	stop = () => {
		if (this.raf) {
			cancelAnimationFrame(this.raf);
			this.raf = null;
		}
	};

	setStopTimeout = () => {
		if (this.timeout) {
			clearTimeout(this.timeout);
			this.timeout = null;
		}
		this.timeout = setTimeout(this.stop, STOP_TIMEOUT_DELAY);
	};

	update = () => {
		Object.values(this.modules).forEach(({ onUpdate }) => onUpdate());
		this.raf = requestAnimationFrame(this.update);
	};

	forceUpdate = () => {
		Object.values(this.modules).forEach(({ onUpdate }) => onUpdate());
	};

	forceResize = () => {
		Object.values(this.modules).forEach(({ onResize }) => onResize());
	};

	render() {
		const { Context, Component } = this;
		const { scrollingElement } = this.state;
		const contextIsReady = !!this.context[this.id];
		const registered = contextIsReady && !!scrollingElement;

		return (
			<Context.Provider value={this.state}>
				<Component
					id={this.id}
					ref={this.scrollerRef}
					className={[SCROLLER_CLASSNAME, this.props.className]}
					style={this.props.scrollingComponentStyles || {}}
					showcta={this.props.showcta}
				>
					{registered && this.props.children}
				</Component>
			</Context.Provider>
		);
	}
}

ScrollerWithContextProvider.propTypes = {
	children: PropTypes.node,
	id: PropTypes.string,
	scrollingElement: PropTypes.oneOfType([PropTypes.node, PropTypes.string]),
	defaultEnabled: PropTypes.bool,
	disabled: PropTypes.bool,
	scrollingComponent: PropTypes.object,
	className: PropTypes.string,
	scrollingComponentStyles: PropTypes.object,
	showcta: PropTypes.bool,
};

export default ScrollerWithContextProvider;
